import React, { useRef, useState } from 'react';
import {
  FIRST_CALL_OUTCOMES, APPOINTMENT_OUTCOME, NO_CONTACT_DISCARD_REASON, todayStr, nextOpenDayStr, toLocalInput,
  telUrl, whatsappUrl, gmailComposeUrl, presentationMessage, canDiscard, rpcError
} from './config';
import { L, Chips, Field } from './ui';
import DiscardControl from './DiscardControl';

// Primer contacto de un lead asignado: siempre una llamada. Se indica si se habló con el cliente:
// - Contactado: resultado, próximo seguimiento, resumen y, si hay cita, día y hora (recordatorio por correo 2 h de tienda antes).
// - No contactado: descartar (sin datos de contacto o incorrectos) o escribirle por WhatsApp / correo con un mensaje de
//   presentación ya redactado. Tras este primer registro, la ficha muestra el "Registrar contacto" de siempre.
export default function FirstCallPanel({ supabase, lead, access, currentUserId, userName, storeName, onDone }) {
  const [result, setResult] = useState('');
  const [outcome, setOutcome] = useState('');
  const [summary, setSummary] = useState('');
  const [next, setNext] = useState(todayStr(1));
  const [appt, setAppt] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const callLogged = useRef(false);

  const tel = telUrl(lead);
  const msg = presentationMessage(lead, userName, storeName);
  const wa = whatsappUrl(lead, msg.body);
  const mail = gmailComposeUrl(lead, msg.subject, msg.body);
  const isAppt = outcome === APPOINTMENT_OUTCOME;

  const rpc = (args) => supabase.rpc('log_lead_interaction', { p_lead_id: lead.id, ...args });

  const saveContacted = async () => {
    if (!next) { setError('Indica la fecha del próximo seguimiento.'); return; }
    if (/[<>]/.test(outcome + summary)) { setError('No se permiten los caracteres < ni >.'); return; }
    let apptIso = null;
    if (isAppt) {
      if (!appt) { setError('Indica el día y la hora de la cita.'); return; }
      const d = new Date(appt);
      if (Number.isNaN(d.getTime()) || d.getTime() < Date.now()) { setError('La cita tiene que ser en el futuro.'); return; }
      apptIso = d.toISOString();
    }
    setBusy('contacted'); setError('');
    const { error: err } = await rpc({
      p_type: 'llamada', p_outcome: `Contactado · ${outcome || 'Sin resultado indicado'}`, p_summary: summary || null,
      p_next_followup: next, p_appointment_at: apptIso
    });
    setBusy('');
    if (err) { setError(rpcError(err)); return; }
    onDone(isAppt ? 'Llamada registrada y cita guardada: te llegará un recordatorio por correo 2 horas antes' : 'Primera llamada registrada');
  };

  // Registra la llamada sin respuesta (una sola vez aunque se pulse más de un botón).
  const logNoContact = async () => {
    if (callLogged.current) return null;
    const { error: err } = await rpc({ p_type: 'llamada', p_outcome: 'No contactado', p_next_followup: nextOpenDayStr() });
    if (!err) callLogged.current = true;
    return err || null;
  };

  const sendPresentation = async (kind) => {
    // El enlace se abre antes de esperar a la base de datos: si no, el navegador bloquea la ventana nueva.
    window.open(kind === 'whatsapp' ? wa : mail, '_blank', 'noopener,noreferrer');
    setBusy(kind); setError('');
    let err = await logNoContact();
    if (!err) {
      ({ error: err } = await rpc({
        p_type: kind, p_outcome: 'Mensaje de presentación enviado', p_summary: 'Tras la llamada sin respuesta.',
        p_next_followup: nextOpenDayStr()
      }));
    }
    setBusy('');
    if (err) { setError(rpcError(err)); return; }
    onDone(kind === 'whatsapp' ? 'Llamada sin respuesta registrada y WhatsApp abierto con el mensaje' : 'Llamada sin respuesta registrada y Gmail abierto con el correo');
  };

  const onlyNoContact = async () => {
    setBusy('nocontact'); setError('');
    const err = await logNoContact();
    setBusy('');
    if (err) { setError(rpcError(err)); return; }
    onDone('Llamada sin respuesta registrada');
  };

  return (
    <div style={{ ...L.section, border: '1px solid var(--l-f59e0b)' }}>
      <h5 style={L.sectionTitle}>📞 Primera llamada</h5>
      <p style={{ ...L.muted, margin: '0 0 10px' }}>
        El primer contacto con un lead es siempre una llamada. {tel ? 'Llama al cliente y' : 'Este lead no tiene teléfono:'} indica el resultado.
      </p>
      {tel && <div style={{ marginBottom: '12px' }}><a href={tel} style={L.button}>📞 Llamar ahora</a></div>}

      <span style={L.label}>¿Has hablado con el cliente?</span>
      <div style={{ marginBottom: '12px' }}>
        <Chips value={result} onChange={(v) => { setResult(v); setError(''); }} options={[
          { key: 'si', label: '✅ Cliente contactado' },
          { key: 'no', label: '📵 Cliente no contactado' }
        ]} />
      </div>

      {result === 'si' && (
        <>
          <span style={L.label}>Resultado de la llamada</span>
          <div style={{ marginBottom: '12px' }}>
            <Chips small value={outcome} onChange={setOutcome} options={FIRST_CALL_OUTCOMES.map(o => ({ key: o, label: o === APPOINTMENT_OUTCOME ? '📅 Cita agendada' : o }))} />
          </div>
          <div style={L.grid2}>
            {isAppt && (
              <Field label="Día y hora de la cita *" htmlFor="lead-first-appt" hint="Te llegará un recordatorio por correo 2 horas de tienda antes.">
                <input id="lead-first-appt" type="datetime-local" style={L.input} value={appt} min={toLocalInput(new Date())}
                  onChange={e => { setAppt(e.target.value); if (e.target.value) setNext(e.target.value.slice(0, 10)); }} />
              </Field>
            )}
            <Field label="Próximo seguimiento *" htmlFor="lead-first-next">
              <input id="lead-first-next" type="date" style={L.input} value={next} min={todayStr()} onChange={e => setNext(e.target.value)} />
            </Field>
          </div>
          <Field label="Resumen de la llamada" htmlFor="lead-first-summary">
            <textarea id="lead-first-summary" style={{ ...L.textarea, minHeight: '60px' }} value={summary} onChange={e => setSummary(e.target.value)} />
          </Field>
          <button type="button" style={L.button} disabled={!!busy || !outcome} onClick={saveContacted}>
            {busy === 'contacted' ? 'Guardando…' : 'Guardar llamada'}
          </button>
          {!outcome && <span style={{ ...L.muted, marginLeft: '8px' }}>Elige un resultado.</span>}
        </>
      )}

      {result === 'no' && (
        <div style={{ display: 'grid', gap: '10px' }}>
          <p style={{ ...L.muted, margin: 0 }}>
            Escríbele con un mensaje de presentación ya redactado (con tu nombre, el coche y el enlace del anuncio), o descarta el lead si
            no tiene datos de contacto o son incorrectos. En los dos casos queda registrada la llamada sin respuesta.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
            <button type="button" style={L.secondary} disabled={!wa || !!busy} onClick={() => sendPresentation('whatsapp')}
              title={wa ? 'Abre WhatsApp con el mensaje escrito' : 'El lead no tiene teléfono'}>
              {busy === 'whatsapp' ? 'Abriendo…' : '💬 Contactar por WhatsApp'}
            </button>
            <button type="button" style={L.secondary} disabled={!mail || !!busy} onClick={() => sendPresentation('email')}
              title={mail ? 'Abre Gmail con el correo escrito' : 'El lead no tiene email'}>
              {busy === 'email' ? 'Abriendo…' : '✉️ Contactar por correo (Gmail)'}
            </button>
            {canDiscard(lead, access, currentUserId) && (
              <DiscardControl supabase={supabase} lead={lead} initialReason={NO_CONTACT_DISCARD_REASON}
                label="🗑️ Descartar: sin datos de contacto o incorrectos" beforeDiscard={logNoContact}
                onDone={() => onDone('Lead descartado')} />
            )}
          </div>
          {(!wa || !mail) && (
            <span style={L.muted}>{!wa && !mail ? 'Sin teléfono ni email válidos.' : !wa ? 'Sin teléfono: no se puede usar WhatsApp.' : 'Sin email: no se puede enviar correo.'}</span>
          )}
          <details>
            <summary style={{ ...L.muted, cursor: 'pointer' }}>Ver el mensaje que se enviará</summary>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.85rem', color: 'var(--t-cbd5e1)', margin: '8px 0 0' }}>{msg.body}</pre>
          </details>
          <div>
            <button type="button" style={{ ...L.secondary, fontSize: '0.8rem', padding: '5px 10px' }} disabled={!!busy} onClick={onlyNoContact}>
              {busy === 'nocontact' ? 'Guardando…' : 'Solo registrar la llamada sin respuesta'}
            </button>
          </div>
        </div>
      )}
      {error && <div style={{ ...L.error, marginTop: '10px' }}>{error}</div>}
    </div>
  );
}
